import { instantAddUtc } from "@redotech/util/temporal";

export enum AgentTimePolicyTier {
  WITHIN_POLICY = "within_policy",
  AGENT_GRACE = "agent_grace",
  MERCHANT_GRACE = "merchant_grace",
  HARD_REJECTION = "hard_rejection",
}

export interface AgentTimePolicyGraceWindows {
  agentGrace: Temporal.Duration;
  merchantGrace: Temporal.Duration;
}

export interface AgentTimePolicyEvaluation {
  tier: AgentTimePolicyTier;
  evaluatedAt: Temporal.Instant;
  policyDeadline: Temporal.Instant;
  agentGraceDeadline: Temporal.Instant;
  merchantGraceDeadline: Temporal.Instant;
}

/** Both grace cutoffs are relative to the policy deadline; cutoffs are exclusive. */
export function evaluateAgentTimePolicy({
  now,
  policyDeadline,
  graceWindows,
}: {
  now: Temporal.Instant;
  policyDeadline: Temporal.Instant;
  graceWindows: AgentTimePolicyGraceWindows;
}): AgentTimePolicyEvaluation {
  if (graceWindows.agentGrace.sign < 0 || graceWindows.merchantGrace.sign < 0) {
    throw new RangeError("Time policy grace windows must be non-negative");
  }

  const agentGraceDeadline = instantAddUtc(
    policyDeadline,
    graceWindows.agentGrace,
  );
  const merchantGraceDeadline = instantAddUtc(
    policyDeadline,
    graceWindows.merchantGrace,
  );
  if (Temporal.Instant.compare(agentGraceDeadline, merchantGraceDeadline) > 0) {
    throw new RangeError("Merchant grace must not end before agent grace");
  }

  let tier: AgentTimePolicyTier;
  if (Temporal.Instant.compare(now, policyDeadline) < 0) {
    tier = AgentTimePolicyTier.WITHIN_POLICY;
  } else if (Temporal.Instant.compare(now, agentGraceDeadline) < 0) {
    tier = AgentTimePolicyTier.AGENT_GRACE;
  } else if (Temporal.Instant.compare(now, merchantGraceDeadline) < 0) {
    tier = AgentTimePolicyTier.MERCHANT_GRACE;
  } else {
    tier = AgentTimePolicyTier.HARD_REJECTION;
  }

  return {
    tier,
    evaluatedAt: now,
    policyDeadline,
    agentGraceDeadline,
    merchantGraceDeadline,
  };
}
